import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import { formatApiDate, formatDisplayDateTime, getSubscriptions } from "../../api/superAdminApi";
import { ROUTE_SUPER_ADMIN } from "../../Util/VMConstants";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import { useAdminPanel } from "./hooks/useAdminPanel";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Not Paid" },
  { value: "paid", label: "Paid" },
];

function statusLabel(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "pending") return "Not Paid";
  if (value === "paid") return "Paid";
  return status || "—";
}

const HEADERS = [
  "#",
  "User ID",
  "Name",
  "Venue",
  "Plan",
  "Tier",
  "Status",
  "Amount",
  "Licenses",
  "Max users",
  "Start",
  "End",
];

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return "—";
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  const formatted = value.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${currency || "INR"} ${formatted}`;
}

function statusColor(status) {
  const value = String(status || "").toLowerCase();
  if (value === "paid" || value === "active") return "success";
  if (value === "pending" || value === "trial") return "warning";
  if (value === "failed" || value === "cancelled" || value === "expired") return "error";
  return "default";
}

export default function VMSuperAdminSubscriptions() {
  const navigate = useNavigate();
  const { canManageAdmins } = useAdminPanel();
  const [searchKey, setSearchKey] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!canManageAdmins) {
      toast.info("Only Super Admin can view subscriptions.");
      navigate(`/${ROUTE_SUPER_ADMIN}`, { replace: true });
    }
  }, [canManageAdmins, navigate]);

  const loadData = useCallback(
    async ({
      page: pageOverride = page,
      rows: rowsOverride = rowsPerPage,
      search = searchKey,
      from = fromDate,
      to = toDate,
      status = statusFilter,
    } = {}) => {
      setLoading(true);
      try {
        const params = {
          page: pageOverride,
          no_of_rows: rowsOverride,
        };
        const term = String(search || "").trim();
        if (term) params.search = term;
        const fromValue = formatApiDate(from);
        const toValue = formatApiDate(to);
        if (fromValue) params.from = fromValue;
        if (toValue) params.to = toValue;
        if (status) params.status = status;
        const res = await getSubscriptions(params);
        setRows(res.data?.data || []);
        setTotalRecords(res.data?.totalCount ?? 0);
      } catch (err) {
        setRows([]);
        setTotalRecords(0);
        const message = err.response?.data?.message || "Subscriptions could not be loaded.";
        toast.info(message);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchKey, fromDate, toDate, statusFilter]
  );

  useEffect(() => {
    if (!canManageAdmins) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageAdmins]);

  return (
    <Box>
      <SuperAdminPageHeader title="Subscription" />

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={setFromDate}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={setToDate}
              format="DD/MM/YYYY"
              minDate={fromDate?.isValid?.() ? fromDate : undefined}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(0);
                  loadData({ page: 0 });
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  loadData({ page: 0 });
                }}
                disabled={loading}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  setStatusFilter("");
                  setSearchKey("");
                  setPage(0);
                  loadData({ page: 0, search: "", from: null, to: null, status: "" });
                }}
                disabled={loading}
              >
                Reset
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {HEADERS.map((header) => (
                <TableCell key={header} sx={{ color: "primary.contrastText", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} aria-label="Loading subscriptions" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 4 }}>
                  No subscriptions found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.id ?? idx} hover>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.venue_id ? (
                      <Link
                        component={RouterLink}
                        to={`/superAdmin/venues/${row.venue_id}`}
                        underline="hover"
                        sx={{ color: "primary.main", fontWeight: 700 }}
                      >
                        {row.user_id}
                      </Link>
                    ) : (
                      row.user_id || "—"
                    )}
                  </TableCell>
                  <TableCell>{row.user_name || "—"}</TableCell>
                  <TableCell>{row.venue_name || "—"}</TableCell>
                  <TableCell>{row.plan_name || "—"}</TableCell>
                  <TableCell>{row.plan_tier || "—"}</TableCell>
                  <TableCell>
                    {row.subscription_status ? (
                      <Chip size="small" label={statusLabel(row.subscription_status)} color={statusColor(row.subscription_status)} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatMoney(row.total_amount ?? row.base_amount, row.currency)}
                  </TableCell>
                  <TableCell>{row.license_count ?? "—"}</TableCell>
                  <TableCell>{row.max_users ?? "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDisplayDateTime(row.start_date)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDisplayDateTime(row.end_date)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, nextPage) => {
                  setPage(nextPage);
                  loadData({ page: nextPage });
                }}
                onRowsPerPageChange={(e) => {
                  const nextSize = parseInt(e.target.value, 10);
                  setRowsPerPage(nextSize);
                  setPage(0);
                  loadData({ page: 0, rows: nextSize });
                }}
                colSpan={HEADERS.length}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
